import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { FilterPanel } from "../FilterPanel";
import { ResourceFilters } from "@features/mx/resources/types";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useGetResources } from "@features/mx/resources/hook";
import { PAGE_SIZE } from "@shared/utils/constants";

export default function RepairedTab() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<ResourceFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  const remoteUtilityId = useRemoteUtilityId();
  const { data: resources, isLoading } = useGetResources(
    remoteUtilityId,
    searchQuery,
    "damaged", // physicalStatusTab
    currentPage,
    PAGE_SIZE,
    filters.utilityService,
    filters.type,
    filters.subType,
    filters.status
  );

  const handleView = (row: any) => {
    console.log("Viewing resource detail from repaired list:", row.id);
    navigate(`/mx/manage/resources/view/${row.id}`);
  };

  const handleEdit = (row: any) => {
    const isWarehouse = row?.physicalStatus === "In Warehouse" || row?.physicalStatusLabel === "In Warehouse";
    const pathType = isWarehouse ? "warehouse" : "installed";
    navigate(`/mx/manage/resources/edit/${pathType}/${row.id}`);
  };

  const actions: TableAction[] = [
    {
      label: "View",
      onClick: handleView,
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Edit",
      onClick: handleEdit,
      icon: "Pencil",
      variant: "outline",
    },
  ];

  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "meterNumber",
        header: "Resource ID",
        type: "text",
        render: (value) => <span className="font-semibold text-gray-900">{value}</span>,
      },
      {
        key: "deviceNo",
        header: "Serial No",
        type: "text",
        render: (value) => <span className="font-semibold text-gray-600">{value}</span>,
      },
      {
        key: "warehouseName",
        header: "Warehouse",
        type: "custom",
        render: (value) => <span className="text-gray-700 font-semibold">{value || "—"}</span>,
      },
      {
        key: "productCode",
        header: "Utility Service",
        type: "custom",
        render: (value) => {
          const colorMap = {
            Electricity: "text-amber-700 bg-amber-50 border-amber-200",
            Water: "text-blue-700 bg-blue-50 border-blue-200",
            Gas: "text-emerald-700 bg-emerald-50 border-emerald-200",
          };
          const cls = colorMap[value as keyof typeof colorMap] || "text-gray-600 bg-gray-50 border-gray-200";
          return <Badge variant="outline" className={cls}>{value}</Badge>;
        },
      },
      {
        key: "resourceTypeDisplay",
        header: "Type",
        type: "text",
      },
      {
        key: "resourceSubtypeDisplay",
        header: "Sub Type",
        type: "text",
      },
      {
        key: "physicalStatusLabel",
        header: "Physical Status",
        type: "custom",
        render: (value) => {
          const colorMap = {
            Installed: "text-indigo-700 bg-indigo-50 border-indigo-250",
            "In Warehouse": "text-sky-700 bg-sky-50 border-sky-200",
            Damaged: "text-red-700 bg-red-50 border-red-200",
            Retired: "text-slate-500 bg-slate-50 border-slate-200",
          };
          const cls = colorMap[value as keyof typeof colorMap] || "text-gray-600 bg-gray-50 border-gray-200";
          return <Badge variant="outline" className={cls}>{value}</Badge>;
        },
      },
      {
        key: "classification",
        header: "Classification",
        type: "custom",
        render: (value, row) => row.classification || row.classificationDisplay || row.ownershipDisplay || row.ownership || "—",
      },
      {
        key: "status",
        header: "Assignment Status",
        type: "custom",
        render: (value) => (
          <Badge
            variant="outline"
            className={
              value === "Assigned" || value === "In Use"
                ? "text-green-700 bg-green-50 border-green-200 font-semibold"
                : "text-slate-500 bg-slate-50 border-slate-200 font-semibold"
            }
          >
            {value}
          </Badge>
        ),
      },
      {
        key: "createdDate",
        header: "Created",
        type: "custom",
        render: (value, row) => (
          <div className="flex flex-col">
            <span className="font-semibold text-gray-900">{value ? value.replace(/-/g, "/") : "—"}</span>
            <p className="text-[10px] text-gray-500 mt-0.5">{row.createdUserRemoteName || "NA"}</p>
          </div>
        ),
      },
    ],
    []
  );

  const totalItems = resources?.count || 0;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
      totalItems: totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalItems]
  );

  return (
    <GenericTable
      data={resources?.results ?? []}
      loading={isLoading}
      columns={columns}
      rowKey="id"
      actions={actions}
      actionsType="icons"
      pagination={pagination}
      onPageChange={(page) => setCurrentPage(page)}
      emptyMessage="No resources found"
      onSearch={(val) => {
        setSearchQuery(val);
        setCurrentPage(1);
      }}
      searchPlaceholder="Search by ID or Serial No..."
      showFilters={showFilters}
      onShowFiltersChange={setShowFilters}
      filterPanel={
        <FilterPanel
          currentFilters={filters}
          onApplyFilters={(newFilters) => {
            setFilters(newFilters);
            setCurrentPage(1);
          }}
          onResetFilters={() => {
            setFilters({});
            setCurrentPage(1);
          }}
        />
      }
    />
  );
}
